import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BoardHeader from './components/BoardHeader';
import BoardColumn from './components/BoardColumn';

import AddColumnModal from './components/AddColumnModal';
import InviteMemberModal from '../../components/modals/InviteMemberModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import useToast from '../../hooks/useToast';

import teamService from '../../utils/teamService';
import ProjectSwitcher from './components/ProjectSwitcher';

const KanbanBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { currentOrganization, userProfile } = useUserProfile();
  const { showToast } = useToast();

  // User state and role - simplified to use single source of truth
  const [currentUser, setCurrentUser] = useState(null);

  // Get user role from current organization (primary) or user profile (fallback)
  const userRole = currentOrganization?.role || userProfile?.role || 'member';

  // Debug role detection
  useEffect(() => {
    console.log('🔍 KANBAN ROLE DEBUG:', {
      currentOrganization: currentOrganization?.role,
      userProfile: userProfile?.role,
      finalRole: userRole
    });
  }, [currentOrganization, userProfile, userRole]);

  // Project context - get from location state, localStorage, or load from API
  const [currentProject, setCurrentProject] = useState(() => {
    // Try to get project from navigation state first
    if (location.state?.projectId && location.state?.project) {
      console.log('🎯 Using project from navigation state:', location.state.project);
      return location.state.project;
    }

    if (location.state?.projectId) {
        console.log('🎯 Found projectId in navigation state, will fetch details for ID:', location.state.projectId);
        return { id: location.state.projectId };
    }

    // Try to get project from localStorage
    try {
      const storedProject = localStorage.getItem('currentProject');
      if (storedProject) {
        const parsed = JSON.parse(storedProject);
        console.log('🎯 Using project from localStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.warn('Failed to parse stored project:', error);
    }

    // Default fallback - will be replaced by API data
    console.log('🎯 Using fallback project data');
    return null;
  });

  // Check if current user is assigned to this project
  const isUserAssignedToProject = useCallback(() => {
    // If no project is loaded yet, assume access for now
    if (!currentProject) {
      return true;
    }

    // Owner always has access
    if (userRole === 'owner') return true;

    // For members and admins, check if they're assigned to this specific project
    // If memberRole is not set, or is 'not-assigned', they don't have access
    return currentProject.memberRole !== 'not-assigned';
  }, [currentProject, userRole]);

  // Determine if the current user can drag/drop cards
  const canDragCard = useCallback((card) => {
    // Owners can always drag cards
    if (userRole === 'owner') return true;

    // Admins can drag cards if they are assigned to the project
    if (userRole === 'admin') {
      return isUserAssignedToProject();
    }

    // Members can drag cards if they are assigned to the project AND are assigned to the card
    if (userRole === 'member') {
      const isAssignedToProject = isUserAssignedToProject();
      
      // Check multiple possible assignment field structures:
      // 1. card.assignments - array of {user_id: ...} objects
      // 2. card.assigned_to - array of user IDs
      const userId = currentUser?.id;
      const isAssignedToCard = 
        card.assignments?.some(a => a.user_id === userId) ||
        card.assigned_to?.includes(userId) ||
        card.assignees?.some(a => a.id === userId || a.user_id === userId);
      
      return isAssignedToProject && isAssignedToCard;
    }

    return false; // Default to no drag permission
  }, [userRole, currentUser, isUserAssignedToProject]);

  // Mock data
  const [board] = useState({
    id: 'board-1',
    title: 'Project Management Board',
    description: 'Main project tracking board for Q4 initiatives',
    isPrivate: false,
    createdAt: '2025-01-15T10:00:00Z',
    updatedAt: '2025-01-28T05:54:23Z',
  });

  // Real members data - will be loaded from team service
  const [members, setMembers] = useState([]);

  // Initialize with empty columns - will be loaded from backend
  const [columns, setColumns] = useState([]);
  // Current board id (needed when creating columns) - initialize with board.id
  const [boardId, setBoardId] = useState(board.id);

  // Initialize with empty cards - will be loaded from backend
  const [cards, setCards] = useState(() => {
    // Try to restore cards from localStorage as fallback
    try {
      const savedCards = localStorage.getItem('kanban-cards');
      return savedCards ? JSON.parse(savedCards) : [];
    } catch (error) {
      console.error('Error loading cards from localStorage:', error);
      return [];
    }
  });

  // Ensure boardId is set when component mounts
  useEffect(() => {
    if (board && board.id && !boardId) {
      setBoardId(board.id);
      console.log('✅ Board ID set to:', board.id);
    }
  }, [board, boardId]);

  // Modal states
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  // Refs for tracking data without triggering re-renders
  const columnsRef = useRef([]);
  
  // Update columnsRef whenever columns change
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isMovingCard, setIsMovingCard] = useState(false);
  
  // Memoize the organization ID to avoid repeated JSON.parse in the render loop
  const getOrganizationId = useCallback(() => {
    if (currentOrganization?.id) return currentOrganization.id;
    try {
      const stored = localStorage.getItem('currentOrganization');
      return stored ? JSON.parse(stored).id : null;
    } catch (e) {
      console.warn('Failed to parse currentOrganization from storage', e);
      return null;
    }
  }, [currentOrganization?.id]);

  const organizationId = getOrganizationId();

  // Handle Project Switch - Memoized
  const handleProjectSwitch = useCallback((project) => {
    if (project.id === currentProject?.id) {
      return;
    }
    
    console.log('🔄 Switching to project:', project.name);
    
    // Update state and navigate
    setCurrentProject(project);
    
    // Update URL to reflect the change
    navigate(`/kanban-board?id=${project.id}`, { 
      state: { project: project },
      replace: true
    });
  }, [currentProject?.id, navigate]);

  // Helper function to load cards for columns (fallback method)
  const loadCardsForColumns = useCallback(async (columnsToLoad, apiService = null, replaceAll = true) => {
    if (!columnsToLoad || columnsToLoad.length === 0) return;

    console.log('🔄 Loading cards for columns (fallback method):', columnsToLoad.map(c => ({ id: c.id, title: c.title })));

    try {
      const kanbanApi = apiService || (await import('../../utils/kanbanApiService')).default;
      let newCards = [];

      for (const column of columnsToLoad) {
        try {
          console.log(`🔧 Loading cards for column: ${column.id} (${column.title || column.name})`);
          const cardsResponse = await kanbanApi.columns.getCards(column.id);
          const cards = cardsResponse?.data || [];

          if (Array.isArray(cards) && cards.length > 0) {
            const transformedCards = cards.map(card =>
              kanbanApi.utils.transformCardData({ data: card })
            ).filter(Boolean);

            newCards = [...newCards, ...transformedCards];
            console.log(`✅ Loaded ${transformedCards.length} cards from column ${column.id}`);
          } else {
            console.log(`📭 No cards found for column ${column.id}`);
          }
        } catch (columnError) {
          console.error(`❌ Error loading cards for column ${column.id}:`, columnError);
        }
      }

      // Update cards state - either replace all or merge with existing
      if (replaceAll) {
        setCards(newCards);
        console.log('✅ All cards replaced:', newCards);
      } else {
        // Partial update - replace cards from these specific columns only
        setCards(prevCards => {
          const columnIds = columnsToLoad.map(col => col.id);
          const otherCards = prevCards.filter(card => !columnIds.includes(card.columnId));
          const updatedCards = [...otherCards, ...newCards];
          console.log('✅ Cards partially updated:', updatedCards);
          return updatedCards;
        });
      }

      // Save to localStorage as backup
      try {
        if (replaceAll) {
          localStorage.setItem('kanban-cards', JSON.stringify(newCards));
        }
      } catch (error) {
        console.error('Error saving cards to localStorage:', error);
      }
    } catch (error) {
      console.error('❌ Error loading cards:', error);
    }
  }, []);

  // Helper function to load all cards for the project (more reliable than column-by-column)
  const loadAllProjectCards = useCallback(async (apiService = null, columnsOverride = null) => {
    if (!currentProject?.id) {
      console.warn('⚠️ No current project ID available for loading cards');
      return;
    }

    // Use provided columns or fall back to ref (avoids dependency on columns state)
    const targetColumns = columnsOverride || columnsRef.current;

    console.log('🔄 Loading all cards for project:', currentProject.id);

    try {
      // Use passed API service or import the Kanban API service
      const kanbanApi = apiService || (await import('../../utils/kanbanApiService')).default;

      // Use the Kanban API to get cards via columns      // We can use the helper function loadCardsForColumns which uses the Kanban API
      console.log(`🔧 Triggering loadCardsForColumns with ${targetColumns.length} columns`);
      if (targetColumns.length === 0) {
        console.warn('⚠️ targetColumns is empty in loadAllProjectCards! This might be why UI is not updating.');
      }
      await loadCardsForColumns(targetColumns, kanbanApi, true);
      console.log('✅ loadAllProjectCards finished');
      
    } catch (error) {
      console.error('❌ Error loading project cards:', error);
    }
  }, [currentProject?.id, loadCardsForColumns]); // Removed columns from dependencies


  // Load user data via unified profile hook
  useEffect(() => {
    if (userProfile) {
      setCurrentUser(userProfile);
    }
  }, [userProfile]);

  // Check for card save/delete success notifications
  useEffect(() => {
    const locationState = window.history.state?.usr;

    if (locationState?.cardSaved && locationState?.savedCardTitle) {
      showToast(
        `Card "${locationState.savedCardTitle}" was saved successfully!`,
        'success',
        4000
      );

      // Clear the state to prevent showing the notification again
      window.history.replaceState(
        { ...window.history.state, usr: { ...locationState, cardSaved: false } },
        '',
        window.location.pathname + window.location.search
      );
    }

    if (locationState?.cardDeleted && locationState?.deletedCardTitle) {
      showToast(
        `Card "${locationState.deletedCardTitle}" was deleted successfully!`,
        'success',
        4000
      );

      // Clear the state to prevent showing the notification again
      window.history.replaceState(
        { ...window.history.state, usr: { ...locationState, cardDeleted: false } },
        '',
        window.location.pathname + window.location.search
      );
    }
  }, [showToast]);




  // Initialize component and ensure immediate loading
  useEffect(() => {
    console.log('🚀 KanbanBoard component mounted, initializing...');

    // Force an immediate check of authentication and project state
    if (isAuthenticated) {
      console.log('✅ User is authenticated, ready to load board');

      // If we have a current project, we're ready to load
      if (currentProject?.id) {
        console.log('✅ Current project available:', currentProject.name);
      } else {
        console.log('🔍 No current project, will fetch from API or localStorage');
      }
    } else {
      console.log('⏳ User not yet authenticated, waiting...');
    }
  }, [isAuthenticated, currentProject?.id, currentProject?.name]); // Added dependencies to resolve ESLint warning

  // Listen for project changes from navigation or localStorage
  useEffect(() => {
    const handleProjectChange = (event) => {
      console.log('🎯 Project change detected:', event.detail);
      const newProject = event.detail?.project;
      if (newProject && newProject.id !== currentProject?.id) {
        console.log('🔄 Updating current project and reloading board');
        setCurrentProject(newProject);
      }
    };

    // Listen for custom project change events
    window.addEventListener('projectChanged', handleProjectChange);

    // Also listen for storage changes in case project is updated in another tab
    const handleStorageChange = (event) => {
      if (event.key === 'currentProject' && event.newValue) {
        try {
          const newProject = JSON.parse(event.newValue);
          if (newProject.id !== currentProject?.id) {
            console.log('🔄 Project updated in localStorage, reloading board');
            setCurrentProject(newProject);
          }
        } catch (error) {
          console.warn('Failed to parse project from localStorage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('projectChanged', handleProjectChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentProject?.id]);

  // Handle navigation state changes and URL parameters
  useEffect(() => {
    console.log('🔍 Checking navigation state and URL for project context');

    // Check if project info is passed via navigation state
    if (location.state?.projectId && location.state?.project) {
      const navProject = location.state.project;
      console.log('🎯 Found project in navigation state:', navProject);

      if (navProject.id !== currentProject?.id) {
        console.log('🔄 Updating current project from navigation state');
        setCurrentProject(navProject);

        // Also update localStorage for consistency
        try {
          localStorage.setItem('currentProjectId', navProject.id);
          localStorage.setItem('currentProject', JSON.stringify(navProject));
        } catch (error) {
          console.warn('Failed to update localStorage:', error);
        }
      }
    }
    // Check URL parameters for project ID
    else {
      const urlParams = new URLSearchParams(location.search);
      const projectIdFromUrl = urlParams.get('projectId') || urlParams.get('id');

      if (projectIdFromUrl && projectIdFromUrl !== currentProject?.id) {
        console.log('🎯 Found project ID in URL:', projectIdFromUrl);
        // We'll let the main loading effect handle fetching the project details
        // This just triggers a reload if the URL project ID is different
      }
    }
  }, [location.state, location.search, currentProject?.id]);

  // Load boards and columns for current project, robust to navigation/reload
  useEffect(() => {
    const loadBoardsAndColumns = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        if (!isAuthenticated) {
          console.log('User not authenticated, clearing board state');
          setColumns([]);
          setBoardId(null);
          setCards([]);
          setIsLoading(false);
          // Clear localStorage backup when not authenticated
          try {
            localStorage.removeItem('kanban-cards');
          } catch (error) {
            console.error('Error clearing localStorage:', error);
          }
          return;
        }

        console.log('🚀 Loading Kanban board for authenticated user');
        const apiService = (await import('../../utils/apiService')).default;
        const kanbanApi = (await import('../../utils/kanbanApiService')).default;

        // Determine which project to use
        let targetProject = currentProject;
        let currentProjectId = currentProject?.id;

        // If no current project is set, fetch projects and use the first one
        if (!currentProjectId) {
          console.log('🔍 No current project set, fetching projects...');
          const projects = await apiService.projects.getAll();
          console.log('🔍 Projects response:', projects);

          if (!projects || projects.length === 0) {
            console.log('❌ No projects found');
            setColumns([]);
            setBoardId(null);
            setCards([]);
            setIsLoading(false);
            setLoadError('No projects found. Please create a project first.');
            return;
          }

          // Use first available project and update current project
          targetProject = projects[0];
          currentProjectId = projects[0].id;
          setCurrentProject(targetProject);
          console.log('✅ Using first available project:', currentProjectId, '- Name:', projects[0].name);
        } else {
          console.log('✅ Using current project:', currentProjectId, '- Name:', currentProject.name);
        }

        // Get or create board for the project using new Kanban API
        console.log('🔧 Getting/creating board for project...');
        const boardResponse = await kanbanApi.board.getOrCreateForProject(currentProjectId);
        const boardData = kanbanApi.utils.transformBoardData(boardResponse);

        if (!boardData) {
          console.log('❌ Failed to get/create board');
          setColumns([]);
          setBoardId(null);
          setCards([]);
          setIsLoading(false);
          setLoadError('Failed to load board. Please try refreshing the page.');
          return;
        }

        console.log('✅ Board loaded:', boardData);
        setBoardId(boardData.id);

        // Load team members for the current organization
        try {
          if (currentOrganization?.id) {
            console.log('🔍 Loading team members for organization:', currentOrganization.id);
            const teamMembers = await teamService.getTeamMembers(currentOrganization.id);
            console.log('✅ Team members loaded:', teamMembers);

            if (teamMembers && Array.isArray(teamMembers) && teamMembers.length > 0) {
              setMembers(teamMembers);
              console.log('✅ Team members set in state:', teamMembers.length, 'members');
            } else {
              console.warn('⚠️ No team members found or empty array returned');
              setMembers([]);

              // Try to reload team members after a short delay
              setTimeout(async () => {
                try {
                  console.log('🔄 Retrying team members load...');
                  const retryMembers = await teamService.getTeamMembers(currentOrganization.id);
                  if (retryMembers && Array.isArray(retryMembers) && retryMembers.length > 0) {
                    console.log('✅ Team members loaded on retry:', retryMembers.length);
                    setMembers(retryMembers);
                  }
                } catch (retryError) {
                  console.error('❌ Retry failed:', retryError);
                }
              }, 2000);
            }
          } else {
            console.warn('⚠️ No current organization found, using empty members array');
            setMembers([]);
          }
        } catch (memberError) {
          console.error('❌ Failed to load team members:', memberError);
          console.error('Error details:', memberError.message, memberError.stack);
          // Set empty array on error to prevent UI issues
          setMembers([]);
        }

        // Set columns from board data
        if (boardData.columns && boardData.columns.length > 0) {
          const sortedColumns = boardData.columns
            .map(column => ({
              ...column,
              title: column.title || column.name || 'Untitled',
              name: column.name || column.title || 'Untitled'
            }))
            .sort((a, b) => (a.order || a.position || 0) - (b.order || b.position || 0));
          setColumns(sortedColumns);
          console.log('✅ Columns loaded:', sortedColumns);

          // Load all cards for the project using the sorted columns directly
          // This avoids the issue where setColumns hasn't updated the state yet
          await loadAllProjectCards(kanbanApi, sortedColumns);
          setIsLoading(false);
        } else {
          console.log('⚠️ No columns found in board');
          setColumns([]);
          setCards([]);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading boards and columns:', error);
        setIsLoading(false);
        setLoadError(`Failed to load board: ${error.message || 'Unknown error'}`);
        // Don't clear existing state on error - this prevents data loss on temporary network issues
        console.log('Keeping existing board state due to error');
      }
    };
    
    // Load immediately if authenticated, otherwise add small delay
    if (isAuthenticated) {
      loadBoardsAndColumns();
    } else {
      const timeoutId = setTimeout(loadBoardsAndColumns, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, currentProject?.id, currentOrganization?.id, loadAllProjectCards]); // Added missing dependencies

  // Debounced load function for WebSocket updates - MUST be defined before the useEffect that uses it
  const debouncedLoadProjectCards = useCallback(() => {
    // Simple debounce implementation
    const timeoutId = setTimeout(() => {
      loadAllProjectCards();
    }, 500); // 500ms debounce
    return () => clearTimeout(timeoutId);
  }, [loadAllProjectCards]);

  // Ref to hold the current timeout cleanup function
  const debounceRef = React.useRef(null);

  // Listen for real-time task updates via WebSocket
  useEffect(() => {
    if (!currentProject?.id || !isAuthenticated) return;

    let cleanup = () => {};

    try {
      // Dynamically import WebSocket service to avoid issues if not available
      const setupWebSocket = async () => {
        try {
          const websocketModule = await import('../../utils/websocketService.js');
          const websocketService = websocketModule.default || websocketModule;

          const handleTaskUpdate = (data) => {
            console.log('🔄 Received task update via WebSocket in Kanban:', data);

            // Check if this update is for our current project
            if (data.payload?.project_id === currentProject?.id) {
              console.log('🎯 Task update is for current project, scheduling reload...');
              
              // Cancel previous pending load if exists
              if (debounceRef.current) {
                debounceRef.current();
              }
              
              // Schedule new load
              debounceRef.current = debouncedLoadProjectCards();
            } else {
              console.log('🔍 Task update for different project:', data.payload?.project_id, 'vs current:', currentProject?.id);
            }
          };

          // Listen for task updates
          websocketService.on('task_update', handleTaskUpdate);

          // Join the project room for real-time updates
          websocketService.joinProject(currentProject?.id);

          cleanup = () => {
            websocketService.off('task_update', handleTaskUpdate);
            if (debounceRef.current) debounceRef.current();
          };
        } catch (wsError) {
          console.warn('WebSocket service not available for real-time updates:', wsError);
        }
      };

      setupWebSocket();
    } catch (error) {
      console.warn('Failed to set up WebSocket listeners:', error);
    }

    return cleanup;
  }, [currentProject?.id, isAuthenticated, loadAllProjectCards, debouncedLoadProjectCards]); // Added debouncedLoadProjectCards dependency


  // Manual refresh function
  const handleManualRefresh = async () => {
    if (columns.length > 0) {
      console.log('🔄 Manual refresh triggered');
      setIsLoading(true);
      try {
        await loadAllProjectCards();
        console.log('✅ Manual refresh completed');
      } catch (error) {
        console.error('❌ Manual refresh failed:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Role-based and project-based permission checks
  const canCreateCards = () => {
    return isUserAssignedToProject();
  };

  /*
  const canEditCards = () => {
    return isUserAssignedToProject();
  };
  */

  /*
  const canDeleteCards = () => {
    return isUserAssignedToProject();
  };
  */

  const canCreateColumns = () => {
    return isUserAssignedToProject();
  };

  const canEditColumns = () => {
    return isUserAssignedToProject();
  };

  const canDeleteColumns = () => {
    return isUserAssignedToProject();
  };

  const canInviteMembers = () => {
    return ['admin', 'owner'].includes(userRole);
  };



  // Note: Card loading is handled by the main loadAllProjectCards function above
  // This prevents duplicate API calls and ensures consistency

  // Handle local optimistic reordering during drag
  const handleCardReorder = useCallback((dragId, sourceColumnId, targetColumnId, hoverIndex) => {
    setCards(prevCards => {
      const movedCard = prevCards.find(c => c.id === dragId);
      if (!movedCard) return prevCards;

      // Only proceed if something actually changed in state logic
      if (movedCard.columnId === targetColumnId && movedCard.position === hoverIndex) {
        return prevCards;
      }

      const otherCards = prevCards.filter(c => c.id !== dragId);
      
      const newCards = otherCards.map(c => {
        // Shifting logic in target column
        if (c.columnId === targetColumnId) {
          if (sourceColumnId === targetColumnId) {
            const oldIndex = movedCard.position;
            if (oldIndex < hoverIndex) {
              if (c.position > oldIndex && c.position <= hoverIndex) return { ...c, position: c.position - 1 };
            } else if (oldIndex > hoverIndex) {
              if (c.position >= hoverIndex && c.position < oldIndex) return { ...c, position: c.position + 1 };
            }
          } else {
            if (c.position >= hoverIndex) return { ...c, position: c.position + 1 };
          }
        } 
        // Shifting logic in source column (if different)
        else if (c.columnId === sourceColumnId) {
          if (c.position > movedCard.position) return { ...c, position: c.position - 1 };
        }
        return c;
      });

      return [...newCards, { ...movedCard, columnId: targetColumnId, position: hoverIndex }];
    });
  }, []);

  // Filter cards based on search and filters
  const filteredCards = cards.filter((card) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const cardTitle = (card.title || '').toLowerCase();
      const cardDescription = (card.description || '').toLowerCase();
      const matchesSearch =
        cardTitle.includes(query) ||
        cardDescription.includes(query) ||
        card.labels?.some((label) => (label.name || '').toLowerCase().includes(query));

      if (!matchesSearch) return false;
    }


    // Priority filter
    if (activeFilters.priority?.length > 0) {
      if (!activeFilters.priority.includes(card.priority)) return false;
    }

    // Assignee filter
    if (activeFilters.assignee?.length > 0) {
      const hasAssignee = card.assignedTo?.some((assigneeId) =>
        activeFilters.assignee.includes(assigneeId)
      );
      if (!hasAssignee) return false;
    }

    // Due date filter
    if (activeFilters.dueDate?.length > 0) {
      const today = new Date();
      const cardDueDate = card.dueDate ? new Date(card.dueDate) : null;

      const matchesDueDate = activeFilters.dueDate.some((filter) => {
        if (filter === 'overdue') {
          return cardDueDate && cardDueDate < today;
        }
        if (filter === 'today') {
          return (
            cardDueDate && cardDueDate.toDateString() === today.toDateString()
          );
        }
        if (filter === 'this-week') {
          const weekFromNow = new Date(
            today.getTime() + 7 * 24 * 60 * 60 * 1000
          );
          return (
            cardDueDate && cardDueDate >= today && cardDueDate <= weekFromNow
          );
        }
        if (filter === 'this-month') {
          const monthFromNow = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            today.getDate()
          );
          return (
            cardDueDate && cardDueDate >= today && cardDueDate <= monthFromNow
          );
        }
        if (filter === 'custom' && activeFilters.customDateRange) {
          const startDate = new Date(activeFilters.customDateRange.start);
          const endDate = new Date(activeFilters.customDateRange.end);
          return (
            cardDueDate && cardDueDate >= startDate && cardDueDate <= endDate
          );
        }
        return false;
      });

      if (!matchesDueDate) return false;
    }

    return true;
  }).sort((a, b) => (a.position || 0) - (b.position || 0));

  // Handle card movement between columns and reordering
  const handleCardMove = async (cardId, sourceColumnId, targetColumnId, targetPosition = null) => {
    // Check permissions for this specific card
    const card = cards.find(c => c.id === cardId);
    if (card && !canDragCard(card)) {
      console.log('You can only move cards you are assigned to (or if you are an admin)');
      showToast('You can only move cards you are assigned to', 'error', 3000);
      return;
    }

    if (isMovingCard) {
      console.log('⏭️ Skipping move: Another move is already in progress');
      return;
    }

    try {
      setIsMovingCard(true);
      console.log(`🔧 Moving card ${cardId} from ${sourceColumnId} to ${targetColumnId} at position ${targetPosition}`);

      // Skip move if it's the same column and same position (or no position provided)
      const existingCard = cards.find(c => c.id === cardId);
      if (sourceColumnId === targetColumnId && targetPosition !== null && existingCard?.position === targetPosition) {
        console.log('⏭️ Skipping move: Card already at target position');
        return;
      }

      // Show loading indicator
      showToast('Moving card...', 'info', 1000);

      // Use new Kanban API for card movement
      const kanbanApi = (await import('../../utils/kanbanApiService')).default;
      const moveResult = await kanbanApi.cards.move(cardId, targetColumnId, targetPosition);

      // Show success message with status change info
      const moveData = moveResult.data || moveResult;
      console.log('✅ Move result data:', moveData);

      if (moveData.status && moveData.old_status !== moveData.status) {
        showToast(`Card moved! Status updated to "${moveData.status}"`, 'success', 3000);
      } else {
        showToast('Card moved successfully!', 'success', 2000);
      }

      // Reload all project cards to get the updated status from backend
      console.log('🔄 Reloading cards to show changes in UI...');
      if (columnsRef.current.length === 0) {
        console.warn('⚠️ columnsRef.current is empty during handleCardMove reload!');
      }
      await loadAllProjectCards(kanbanApi);
      console.log('✅ handleCardMove completed and UI should be updated');
    } catch (error) {
      console.error('❌ Failed to move card:', error);
      showToast(`Failed to move card: ${error.message}`, 'error', 4000);
    } finally {
      setIsMovingCard(false);
    }
  };

  // Handle card click - navigate to card details
  const handleCardClick = (card) => {
    console.log('Navigating to card details:', card);

    // Build return path for proper back navigation
    const returnPath = location.pathname + location.search;

    // Navigate with card data in state and URL params
    navigate(`/card-details?id=${card.id || card.card_id || card.cardId}&projectId=${currentProject?.id || ''}`, {
      state: {
        card: card,
        members: members,
        project: currentProject,
        projectId: currentProject?.id,
        returnPath: returnPath,
      },
    });
  };

  // Handle adding new card
  const handleAddCard = (columnId) => {
    // Check authentication first
    if (!isAuthenticated) {
      console.log('Please log in to create cards');
      navigate('/login');
      return;
    }

    if (!canCreateCards()) {
      showToast('You do not have permission to create cards in this project', 'error');
      return;
    }

    console.log('Navigating to card creation for column ID:', columnId);
    
    const returnPath = location.pathname + location.search;
    
    navigate(`/card-details?mode=create&columnId=${columnId}&projectId=${currentProject?.id || ''}`, {
      state: {
        members: members,
        project: currentProject,
        projectId: currentProject?.id,
        returnPath: returnPath,
      },
    });
  };

  // Handle adding new column
  const handleSaveColumn = async (newColumn) => {
    if (!canCreateColumns()) {
      console.log(
        'You can only create columns in projects you are assigned to'
      );
      return;
    }

    // Check if board is loaded
    if (!boardId) {
      console.error('❌ Cannot create column: Board not loaded yet');
      showToast('Please wait for the board to load before creating columns', 'info');
      return;
    }

    try {
      console.log('🔧 Creating column:', newColumn, 'for board:', boardId);

      // Use new Kanban API to create column
      const kanbanApi = (await import('../../utils/kanbanApiService')).default;
      const result = await kanbanApi.columns.create(boardId, {
        name: newColumn.title || newColumn.name,
        color: newColumn.color || '#E5E7EB'
      });

      const createdColumn = result?.data || result;
      console.log('✅ Column created via API:', createdColumn);

      if (createdColumn && createdColumn.id) {
        // Add the backend-created column to frontend state
        const frontendColumn = {
          id: createdColumn.id,
          title: createdColumn.name || createdColumn.title || 'Untitled',
          name: createdColumn.name || createdColumn.title || 'Untitled',
          status: 'todo',
          order: createdColumn.position || createdColumn.order || 0,
          position: createdColumn.position || createdColumn.order || 0,
          color: createdColumn.color || '#E5E7EB'
        };
        setColumns((prevColumns) => [...prevColumns, frontendColumn]);
        console.log('✅ Column added to UI:', frontendColumn);
        showToast(`Column "${frontendColumn.title}" created!`, 'success');
      } else {
        showToast('Failed to create column: Invalid response from server', 'error');
      }
    } catch (error) {
      console.error('❌ Failed to create column:', error);

      // Provide more specific error messages
      let errorMessage = 'Unknown error occurred';
      if (error.message.includes('500')) {
        errorMessage = 'Server error - please try again in a moment';
      } else if (error.message.includes('403')) {
        errorMessage = 'You do not have permission to create columns';
      } else if (error.message.includes('404')) {
        errorMessage = 'Board not found - please refresh the page';
      } else {
        errorMessage = error.message;
      }

      showToast(`Failed to create column: ${errorMessage}`, 'error');
    }
  };

  // Handle column operations
  const handleEditColumn = (columnId, updates) => {
    if (!canEditColumns()) {
      console.log(
        'You can only edit columns in projects you are assigned to'
      );
      return;
    }
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId
          ? { ...col, ...updates, updatedAt: new Date().toISOString() }
          : col
      )
    );
  };

  const handleDeleteColumn = async (columnId) => {
    if (!canDeleteColumns()) {
      console.log(
        'You can only delete columns in projects you are assigned to'
      );
      return;
    }

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this column? All cards in this column will be moved to the first column.')) {
      return;
    }

    try {
      console.log(`🔧 Deleting column ${columnId}`);

      // First, move cards from deleted column to first column if needed
      const firstColumnId = columns[0]?.id;
      const cardsToMove = cards.filter(card => card.columnId === columnId);

      if (firstColumnId && firstColumnId !== columnId && cardsToMove.length > 0) {
        console.log(`🔧 Moving ${cardsToMove.length} cards to first column before deletion`);

        // Use kanban API to move cards
        const kanbanApi = (await import('../../utils/kanbanApiService')).default;

        for (const card of cardsToMove) {
          try {
            await kanbanApi.cards.move(card.id, firstColumnId);
            console.log(`✅ Moved card ${card.id} to column ${firstColumnId}`);
          } catch (error) {
            console.error(`❌ Failed to move card ${card.id}:`, error);
          }
        }
      }

      // Delete the column via API
      const apiService = (await import('../../utils/apiService')).default;
      await apiService.columns.delete(columnId);

      console.log('✅ Column deleted successfully');

      // Update local state
      setColumns((prevColumns) =>
        prevColumns.filter((col) => col.id !== columnId)
      );

      // Update cards state to reflect moved cards
      if (firstColumnId && firstColumnId !== columnId) {
        setCards((prevCards) =>
          prevCards.map((card) =>
            card.columnId === columnId
              ? { ...card, columnId: firstColumnId }
              : card
          )
        );
      }

    } catch (error) {
      console.error('❌ Failed to delete column:', error);
      showToast(`Failed to delete column: ${error.message}`, 'error');
    }
  };

  // Handle member invitation
  const handleMemberInvite = () => {
    if (!canInviteMembers()) {
      console.log('Only admins and owners can invite members');
      return;
    }
    setShowInviteMemberModal(true);
  };

  const handleSendInvitation = async (inviteData) => {
    try {
      console.log('Sending invitations:', inviteData);
      const orgId = currentOrganization?.id || JSON.parse(localStorage.getItem('currentOrganization') || '{}').id;
      
      if (!orgId) {
        showToast('No organization selected', 'error');
        return;
      }

      for (const email of inviteData.emails) {
        await teamService.inviteTeamMember(orgId, {
          email,
          role: inviteData.role,
          message: inviteData.welcomeMessage
        });
      }
      
      showToast(`Successfully invited ${inviteData.emails.length} member(s)`, 'success');
      // Refresh members list
      const updatedMembers = await teamService.getTeamMembers(orgId);
      setMembers(updatedMembers);
    } catch (error) {
      console.error('Failed to send invitations:', error);
      showToast('Failed to send invitations', 'error');
    }
  };

  // Handle board updates
  const handleBoardUpdate = (updates) => {
    console.log('Board updated:', updates);
    // In real app, this would update the board via API
  };

  // Get cards for a specific column
  const getCardsForColumn = (columnId) => {
    return filteredCards.filter((card) => card.columnId === columnId);
  };

  // Show login prompt if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4'>
            <Icon name='Kanban' size={32} className='text-primary-foreground' />
          </div>
          <h1 className='text-2xl font-bold text-foreground mb-2'>
            Authentication Required
          </h1>
          <p className='text-muted-foreground mb-6'>
            Please log in to access the project management board.
          </p>
          <Button onClick={() => navigate('/login')} className='px-6 py-2'>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-transparent'>
      <RoleBasedHeader
        userRole={(userRole || 'member').toLowerCase()}
        currentUser={
          currentUser
            ? {
                id: currentUser.id,
                name: currentUser.name || `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'User',
                email: currentUser.email,
                role: userRole,
                avatar: currentUser.avatar
              }
            : {
                name: 'Loading...',
                email: '',
                role: userRole,
              }
        }
        />

        <main className='pt-20'>
          <div className='max-w-full px-4 sm:px-8 lg:px-10 py-8'>
            <div className="mb-6">
              <Breadcrumb projectName={currentProject?.name} />
            </div>

            {/* Enhanced Page Header */}
            <div className='mb-8 relative z-10'>
              <div className='glass-surface rounded-3xl p-8'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center space-x-6'>
                    <div className='w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-xl'>
                      <Icon
                        name='Kanban'
                        size={28}
                        className='text-primary-foreground'
                      />
                    </div>
                    <div>
                      {/* Dynamic Project Title & Dropdown Component */}
                      <ProjectSwitcher 
                        currentProject={currentProject} 
                        currentOrganizationId={organizationId}
                        onProjectSwitch={handleProjectSwitch}
                      />
                      <p className='text-slate-500 dark:text-slate-400 mt-1 font-bold uppercase tracking-[0.15em] text-[10px]'>
                        {currentProject?.description || 'Active Workflow Management'}
                      </p>
                    </div>
                  </div>

                  {/* Board Stats */}
                  <div className='hidden md:flex items-center space-x-10'>
                    <div className='text-center'>
                      <div className='text-3xl font-black text-primary tracking-tightest'>
                        {columns.length}
                      </div>
                      <div className='text-[10px] font-black uppercase tracking-widest text-slate-400'>Columns</div>
                    </div>
                    <div className='text-center'>
                      <div className='text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tightest'>
                        {cards.length}
                      </div>
                      <div className='text-[10px] font-black uppercase tracking-widest text-slate-400'>Tasks</div>
                    </div>
                    <div className='text-center'>
                      <div className='text-3xl font-black text-rose-600 dark:text-rose-400 tracking-tightest'>
                        {cards.filter(card => card.priority === 'high' || card.priority === 'urgent').length}
                      </div>
                      <div className='text-[10px] font-black uppercase tracking-widest text-slate-400'>Priority</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Board Content */}
            {!isLoading && !loadError && (
              <div className='flex-1 p-2 overflow-hidden'>
                <BoardHeader
                  board={board}
                  members={members}
                  onBoardUpdate={handleBoardUpdate}
                  onMemberInvite={handleMemberInvite}
                  onFilterChange={setActiveFilters}
                  onSearchChange={setSearchQuery}
                  onRefresh={handleManualRefresh}
                  searchQuery={searchQuery}
                  activeFilters={activeFilters}
                  canInviteMembers={canInviteMembers()}
                  organizationName={
                    (currentOrganization && currentOrganization.name) ||
                    'Organization'
                  }
                />
                
                <div className='mt-6 flex space-x-6 overflow-x-auto pb-8 h-full scrollbar-thin' style={{ minHeight: 'calc(100vh - 400px)' }}>
                {/* Columns */}
                {columns
                  .sort((a, b) => a.order - b.order)
                  .map((column) => (
                    <BoardColumn
                      key={column.id}
                      column={column}
                      cards={getCardsForColumn(column.id)}
                      onCardMove={handleCardMove}
                      onCardReorder={handleCardReorder}
                      onCardClick={handleCardClick}
                      onAddCard={() => handleAddCard(column.id)}
                      onEditColumn={handleEditColumn}
                      onDeleteColumn={handleDeleteColumn}
                      members={members}
                      canCreateCards={canCreateCards()}
                      canEditColumns={canEditColumns()}
                      canDeleteColumns={canDeleteColumns()}
                      canDragCard={canDragCard}
                    />
                  ))}
              </div>

              {/* Empty State */}
              {filteredCards.length === 0 && searchQuery && (
                <div className='glass-surface rounded-2xl p-12 text-center mt-8'>
                  <Icon
                    name='Search'
                    size={48}
                    className='text-slate-300 mx-auto mb-4'
                  />
                  <h3 className='text-xl font-black text-slate-800 dark:text-slate-200 mb-2'>
                    No results found
                  </h3>
                  <p className='text-slate-500 max-w-md mx-auto'>
                    We couldn't find any cards matching "{searchQuery}". Try a different search term.
                  </p>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setSearchQuery('');
                      setActiveFilters({});
                    }}
                    className='mt-6 rounded-xl'
                  >
                    Reset Dashboard
                  </Button>
                </div>
              )}
              </div>
            )}
            
            {/* Loading/Error states kept consistent */}
            {isLoading && (
              <div className='flex items-center justify-center py-24'>
                <div className='flex flex-col items-center'>
                  <div className='animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4'></div>
                  <p className='text-slate-500 font-medium'>Loading your workspace...</p>
                </div>
              </div>
            )}

            {loadError && !isLoading && (
              <div className='flex flex-col items-center justify-center py-24 px-4'>
                <div className='w-20 h-20 bg-rose-50 dark:bg-rose-900/20 rounded-full flex items-center justify-center mb-6'>
                  <Icon name='AlertCircle' size={40} className='text-rose-500' />
                </div>
                <h2 className='text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2'>
                  Unable to load board
                </h2>
                <p className='text-slate-500 dark:text-slate-400 text-center max-w-md mb-8'>
                  {loadError}
                </p>
                <div className='flex space-x-4'>
                  <Button 
                    onClick={() => window.location.reload()}
                    className='rounded-xl px-6'
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant='outline'
                    onClick={() => navigate('/dashboard')}
                    className='rounded-xl px-6'
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            )}

            {/* Modals are handled below */}

            <AddColumnModal
              isOpen={showAddColumnModal}
              onClose={() => setShowAddColumnModal(false)}
              onSave={handleSaveColumn}
            />

            <InviteMemberModal
              isOpen={showInviteMemberModal}
              onClose={() => setShowInviteMemberModal(false)}
              onInvite={handleSendInvitation}
              organizationId={currentOrganization?.id}
              existingMemberIds={(members || []).filter(m => m).map(m => m.user_id || m.id)}
              existingMemberEmails={(members || []).filter(m => m).map(m => m.email || '')}
            />
          </div>
        </main>
      </div>
  );
};

export default KanbanBoard;
